import { useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { getMainRouteConfigForLocation } from '@/config/mainRoutes'
import { resolveTabLocation } from '@/config/routeManifest'
import { useMountEffect } from '@/hooks/useMountEffect'
import { setShellActiveTabId } from '@/components/layout/shellActiveTab'
import { registerStorageSync, useAppStore } from '@/stores/app'
import { useTabDrafts } from '@/stores/tabDrafts'

/**
 * Bridges react-router and `useAppStore`. Mount once near the top of the
 * authenticated layout. On every URL change, idempotently:
 *
 *  1. Ensure the current tab id exists in `tabs.items` (open if absent).
 *  2. Apply the authoritative title when the schema provides one — handles
 *     the cold-paste-then-state-navigate case where a placeholder needs to
 *     be replaced by the real title.
 *  3. Record the tab's latest href — canonical pathname plus the task-owned
 *     query and hash (shell/ephemeral keys stripped) — so a later tab switch
 *     restores the tab's own filters.
 *  4. Prune stale `closingTabIds` markers for tab ids that are no longer
 *     active (post-render effect; runs after the close coordinator navigated
 *     away and after the unmounted editor's cleanup has already read
 *     isTabClosing).
 *
 * The active tab is NOT written anywhere — it is derived from the URL by
 * consumers (useAppTabs / ContentPanelArea).
 *
 * Writes happen in `useLayoutEffect` (post-commit, pre-paint) rather than
 * during render. Render-time writes raced with useSyncExternalStore's
 * snapshot consistency, producing visible URL/tab-state mismatches on
 * routes with active search params (e.g. /sessions). All dispatches are
 * idempotent — strict-mode double commits are safe.
 *
 * Also owns the cross-tab storage-rehydration listener — registered here
 * on mount rather than at module load so test environments don't fire
 * `addEventListener` on every import.
 */
export function AppShellEffects() {
  const location = useLocation()
  const resolved = resolveTabLocation(location)
  const config = getMainRouteConfigForLocation({
    pathname: location.pathname,
    state: location.state,
  })
  const configTitle = config?.title
  const authoritativeTitle = config?.authoritativeTitle
  const tabId = resolved?.id ?? null
  const routeId = resolved?.routeId ?? null
  const href = resolved?.href ?? null

  useLayoutEffect(() => {
    if (!configTitle || !tabId || !routeId || !href) return
    const store = useAppStore.getState()
    store.openTab({ id: tabId, routeId, title: configTitle, href })
    if (authoritativeTitle) {
      store.renameTab(tabId, authoritativeTitle)
    }
    store.updateTabHref(tabId, href)
  }, [tabId, routeId, href, configTitle, authoritativeTitle])

  // Publish which tab is visible for the readers that live outside React —
  // the editor registry reads it to tell a card leaving a VISIBLE tab from
  // <Activity> tearing down a backgrounded one. Layout effect, same commit as
  // the tab sync above, so it is current before any passive cleanup runs.
  useLayoutEffect(() => {
    setShellActiveTabId(tabId)
    return () => setShellActiveTabId(null)
  }, [tabId])

  useEffect(() => {
    const drafts = useTabDrafts.getState()
    for (const id of drafts.closingTabIds) {
      if (id !== tabId) drafts.unmarkClosing(id)
    }
  }, [tabId])

  useMountEffect(() => {
    registerStorageSync()
  })

  // Last line of defence for unsaved work: reload, back out of the SPA, or
  // close the window with a dirty draft and the browser asks first. The tab
  // close path has its own dialog; this covers the ones we cannot intercept.
  //
  // Coverage: the editors that persist drafts through useEditorDraft
  // (documents, meeting notes), plus the composite tabs whose registered cards
  // raise the tab's dirty flag — a duty tab's script, Conversation, Fast First
  // Message and Handoff cards, and an eval case's counterparty prompt. The
  // remaining duty/eval cards each carry a follow-up TODO at the fix site.
  useMountEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!useTabDrafts.getState().hasDirty()) return
      // Browsers show their own generic wording; no custom text is possible.
      event.preventDefault()
      // Safari and older Chromium only raise the prompt for a set returnValue.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  })

  return null
}
